import {
  type QueryClient,
  queryOptions,
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { analytics, filterCountBucket, METRICS } from "~/analytics";
import type { AppointmentListFiltersInput, AppointmentRecord } from "~/api/API";
import { appointmentListViewOptInAtom } from "~/atoms/appointmentListView";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { isAuthCredentialError } from "~/auth/authErrors";
import { useCurrentUser } from "~/auth/UserContext";
import { UnauthorizedAccessError } from "~/error-handling";
import { withErrorReporting } from "~/error-handling/reactQueryErrorUtil";
import type { ListViewParams } from "~/pages/components/appointmentListView.helpers";
import { fetchSchedulerAssistantAppointments } from "~/pages/components/schedulerAssistantCalendar/appointmentFetchUtils";
import {
  AppointmentSearchParams,
  appointmentService,
} from "~/services/appointment";
import {
  appointmentListService,
  SortingOption,
} from "~/services/appointmentList";
import {
  AppointmentListViewCursorError,
  AppointmentListViewRetryableError,
  fetchAppointmentListView,
  type ListViewChecklistStatuses,
} from "~/services/appointmentListView";
import { FEATURE_FLAGS } from "~/services/featureFlags/constants";
import { useFeatureFlag } from "~/services/featureFlags/provider";
import { useIsProdEnv } from "~/state/atoms";
import { AuthUserGroup } from "~/types/auth";
import {
  appointmentFilesQueryConfig,
  type FileDisplayData,
} from "./useAppointmentFiles";
import {
  appointmentFilesBatchQueryConfig,
  useBatchFileReads,
} from "./useAppointmentFilesBatch";

export const appointmentKeys = {
  all: ["appointments"] as const,
  lists: () => [...appointmentKeys.all, "list"] as const,
  infiniteLists: () => [...appointmentKeys.all, "infiniteList"] as const,
  infiniteList: (params: AppointmentListParams) =>
    [...appointmentKeys.infiniteLists(), params] as const,
  search: (params: AppointmentSearchParams) =>
    [...appointmentKeys.all, "search", params] as const,
  detail: (id: string) => [...appointmentKeys.all, id] as const,
  scheduler: (physicianId: string | null, startDate: string, endDate: string) =>
    [
      ...appointmentKeys.all,
      "scheduler",
      physicianId,
      startDate,
      endDate,
    ] as const,
  // Range-free key for the V2 scheduler's infinite appointments query. Parented
  // under `all` so the shared appointment-mutation invalidation
  // (`invalidateQueries(appointmentKeys.all)`) also refreshes the scheduler —
  // otherwise a reschedule/new booking wouldn't reach it and the "Appointment
  // days" list would show a stale date until staleTime/refocus.
  schedulerAppointments: (physicianId: string | null) =>
    [...appointmentKeys.all, "schedulerAppointments", physicianId] as const,
  schedulerWeek: (physicianId: string | null, weekStartISO: string) =>
    [
      ...appointmentKeys.all,
      "scheduler-week",
      physicianId,
      weekStartISO,
    ] as const,
};

export interface AppointmentListParams {
  sorting: SortingOption;
  startDate?: string;
  endDate?: string;
  includeEmptyDates?: boolean;
  inquiryStartDate?: string;
  inquiryEndDate?: string;
  filters?: AppointmentListFiltersInput;
  /**
   * The same page as a search-service list-view request. Read only when the
   * list-view flag is on (`readPath === "opensearch"`); the DynamoDB path
   * ignores it.
   */
  listView?: ListViewParams;
}

/** Which backend a list page is read from. */
export type AppointmentListReadPath = "dynamo" | "opensearch";

export interface AppointmentListPage {
  appointments: AppointmentRecord[];
  /** DynamoDB `nextToken` or the list view's `next_cursor`. */
  nextToken: string | null;
  /** Every match in scope; the list view only. */
  total?: number;
  /** Each row's checklist cell statuses; the list view only. */
  checklist?: ListViewChecklistStatuses;
  /** Each row's case readiness on the views that read it; the list view only. */
  caseReady?: Record<string, boolean>;
}

/** Checklist statuses by appointment id, or `null` off the list view. */
export type ServedChecklist = ReadonlyMap<
  string,
  Readonly<Record<string, string>>
> | null;

/** Case readiness by appointment id (spec D9), or `null` off the list view. */
export type ServedCaseReady = ReadonlyMap<string, boolean> | null;

/** Rows the browser holds per list-view page (spec D8). */
export const LIST_VIEW_PAGE_SIZE = 300;

/**
 * Optimistically apply a change to an appointment's cached DETAIL record, and
 * return a rollback for the mutation's error path.
 *
 * Drawers read the detail cache (`useAppointment` / `useAppointmentWithFiles`),
 * so an optimistic write that only touches the infinite-list cache or the
 * selected-appointment atom never reaches them — the control appears
 * unresponsive until the mutation settles and the detail query refetches.
 * Callers that write the list cache for the dash column should call this too.
 *
 * The patch runs as a **functional updater** so it applies to whatever is
 * cached: the full record once the drawer has fetched it, or the shallow row
 * `useAppointmentsList` seeded. Writing a shallow row here directly would
 * downgrade a cached full record and blank fields the drawer just gained.
 * (`setQueryData<T | null>` also requires the functional form on react-query
 * 5.9x+ — a direct object literal is rejected by the `NoInfer` wrapper.)
 *
 * No-ops when nothing is cached for the id; the drawer will fetch it fresh.
 *
 * Cancels any in-flight detail fetch first: one that resolves *after* the patch
 * would overwrite it with pre-mutation data, and the optimistic path has no
 * invalidation afterwards to self-correct. Both cache calls are `exact` so a
 * prefix-extended sibling query (`usePriorOpinionRecap` keys on
 * `[...detail(id), "opinionRecap"]`) is never cancelled or evicted alongside.
 *
 * Last-write-wins across concurrent mutations on the SAME appointment: if A
 * patches, B patches on top, then A fails, A's rollback restores the snapshot
 * it took before B ran and discards B's optimistic write. Accepted — the
 * drawer edits one appointment at a time, and the mutation's own invalidation
 * reconciles shortly after.
 */
export async function patchAppointmentDetailCache(
  queryClient: QueryClient,
  appointmentId: string,
  patch: (appointment: AppointmentRecord) => AppointmentRecord,
): Promise<() => void> {
  const queryKey = appointmentKeys.detail(appointmentId);
  await queryClient.cancelQueries({ queryKey, exact: true });
  const previous = queryClient.getQueryData<AppointmentRecord | null>(queryKey);

  queryClient.setQueryData<AppointmentRecord | null>(queryKey, (current) =>
    current ? patch(current) : current,
  );

  return () => {
    if (previous === undefined) {
      // Nothing was cached before us — drop what we wrote rather than pinning
      // a fabricated entry that no fetch produced.
      queryClient.removeQueries({ queryKey, exact: true });
      return;
    }
    queryClient.setQueryData<AppointmentRecord | null>(
      queryKey,
      () => previous,
    );
  };
}

/**
 * The detail query for one appointment. `useAppointment` and the drawers'
 * neighbour prefetch (`usePrefetchAppointmentDetails`) share it, so a prefetch
 * fills the cache entry the drawer then reads, with the same selection set.
 */
export const appointmentDetailQueryOptions = (
  id: string,
  userGroup: AuthUserGroup | null | undefined,
) =>
  queryOptions({
    queryKey: appointmentKeys.detail(id),
    queryFn: async () => {
      if (!id) {
        return null;
      }

      return withErrorReporting(
        () =>
          appointmentService.fetchAppointmentById(id, userGroup ?? undefined),
        {
          componentName: "AppointmentDetail",
          operationName: "fetch-appointment-by-id",
          extra: { appointmentId: id },
        },
      );
    },
    // Don't retry authorization errors — the result won't change on retry.
    // A per-query `retry` fully REPLACES the QueryClient default rather than
    // merging with it, so this has to restate the default's auth-error rule
    // (see `queryClient` in ~/lib/react-query): a credential failure is
    // recovered by the GraphQL client's refresh-and-retry shell, so if one
    // still reaches here recovery already failed, and blind-retrying just
    // re-amplifies the ~3s NoSignedUser storm (#7299). It also delays the
    // expired-session banner by three backoff rounds.
    retry: (failureCount, error) => {
      if (error instanceof UnauthorizedAccessError) return false;
      if (isAuthCredentialError(error)) return false;
      return failureCount < 3;
    },
    // Settings staleTime to 30 seconds to dedupe requests on the same page
    // TODO: Once we've implemented proper subscription then we can potentially set this to be much longer,
    // and any invalidation will be handled by the subscription.
    staleTime: 30 * 1000, // 30 seconds
  });

export const useAppointment = (id?: string) => {
  const { userGroup } = useAuthSession();

  return useQuery({
    ...appointmentDetailQueryOptions(id ?? "", userGroup),
    enabled: !!id,
  });
};

/**
 * Resolve the most recent prior appointment's persisted OpinionRecap.
 *
 * Mirrors the backend `resolve_previous_opinion_source`: candidate prior
 * appointments are the supplemental sources plus the original appointment;
 * we keep only those that actually have a generated report and pick the most
 * recent by date. Returns that appointment's OpinionRecap (or null).
 */
export const usePriorOpinionRecap = (appointment: AppointmentRecord) => {
  const candidateIds = useMemo(() => {
    const ids = new Set<string>();
    (appointment.supplementalSourceAppointmentIDs ?? []).forEach((id) => {
      if (id) ids.add(id);
    });
    if (appointment.originalAppointmentID) {
      ids.add(appointment.originalAppointmentID);
    }
    ids.delete(appointment.id); // never recap the current appointment
    return [...ids];
  }, [
    appointment.id,
    appointment.originalAppointmentID,
    appointment.supplementalSourceAppointmentIDs,
  ]);

  const results = useQueries({
    queries: candidateIds.map((id) => ({
      queryKey: [...appointmentKeys.detail(id), "opinionRecap"] as const,
      queryFn: () =>
        withErrorReporting(
          () => appointmentService.fetchAppointmentOpinionRecap(id),
          {
            componentName: "AssessmentWorkspaceTab",
            operationName: "fetch-prior-opinion-recap",
            extra: { appointmentId: id },
          },
        ),
      staleTime: 30 * 1000,
    })),
  });

  return useMemo(() => {
    const withReports = results
      .map((r) => r.data)
      .filter((a): a is AppointmentRecord => Boolean(a?.reportGenerationFileID))
      .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
    const sourceAppointment = withReports[0] ?? null;
    return {
      sourceAppointment,
      opinionRecap: sourceAppointment?.opinionRecap ?? null,
      isLoading: results.some((r) => r.isLoading),
    };
  }, [results]);
};

export const useAppointmentSearch = (
  params: AppointmentSearchParams,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: appointmentKeys.search(params),
    queryFn: async () => {
      return withErrorReporting(
        () => appointmentService.searchAppointments(params),
        {
          componentName: "AppointmentSearch",
          operationName: "search-appointments",
          extra: {
            hasQuery: !!params.query,
            userGroup: params.userGroup,
            hasDoctorId: !!params.doctorId,
          },
        },
      );
    },
    enabled: enabled && !!params.query,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Cache key for the appointment list query.
 *
 * Doctors fetch a single unfiltered page set via `listAppointmentsForDoctor`
 * (which ignores `params.filters`) and filter entirely client-side. Keying the
 * cache on `filters` would therefore mint a new cache entry — and reset the
 * infinite query to page 1 — on every filter toggle, even though the underlying
 * data is identical. That page-1 reset is what made sparse types (e.g.
 * Supplementals) disappear when filtered (GET-3041). Drop `filters` from the
 * doctor key so toggles reuse the loaded pages and re-filter in place. The
 * server-filtered (non-doctor) path must keep `filters` in the key for
 * correctness.
 *
 * Exported because callers that need to know when a *new* fetch sequence began
 * (see `DASHVIEW_PAGES_TO_FIRST_ROWS`) must derive that from the same key this
 * query uses. Re-deriving it by hand drifts — omitting `filters` silently
 * suppressed the metric on every filter change.
 */
export const appointmentListQueryKey = (
  params: AppointmentListParams,
  userGroup: AuthUserGroup | null,
  readPath: AppointmentListReadPath,
) => {
  // The list view's request is `listView` alone (plus the sort, which it
  // carries too); the DynamoDB key stays what it was before `listView`.
  if (readPath === "opensearch") {
    return appointmentKeys.infiniteList({
      sorting: params.sorting,
      listView: params.listView,
    });
  }
  const dynamoParams = { ...params, listView: undefined };
  return userGroup === AuthUserGroup.DOCTOR
    ? appointmentKeys.infiniteList({ ...dynamoParams, filters: undefined })
    : appointmentKeys.infiniteList(dynamoParams);
};

/**
 * The list-view flag, resolved once per list. `readPath` is "opensearch" only
 * when the caller's flag is on AND the caller built `listView`; a caller
 * without one (the scheduler's range fetches) stays on DynamoDB. Staff also
 * need the per-browser opt-in from User settings; physicians roll out on
 * their own flag alone.
 */
const useAppointmentListReadPath = (
  params: AppointmentListParams,
  userGroup: AuthUserGroup | null,
): { readPath: AppointmentListReadPath; loading: boolean } => {
  const staffFlag = useFeatureFlag(FEATURE_FLAGS.APPT_LIST_VIEW_BY_OPENSEARCH);
  const physicianFlag = useFeatureFlag(
    FEATURE_FLAGS.APPT_LIST_VIEW_PHYSICIAN_OPENSEARCH,
  );
  const staffOptIn = useAtomValue(appointmentListViewOptInAtom);
  const isDoctor = userGroup === AuthUserGroup.DOCTOR;
  const { loading } = isDoctor ? physicianFlag : staffFlag;
  const isEnabled = isDoctor
    ? physicianFlag.isEnabled
    : staffFlag.isEnabled && staffOptIn;
  return {
    readPath: isEnabled && params.listView ? "opensearch" : "dynamo",
    loading,
  };
};

/**
 * List-view retries: a partial-shard answer (502) gets one more try, a
 * rejected cursor restarts from page 1 instead (below), and anything else
 * follows the client default (`~/lib/react-query`): never a credential
 * failure, otherwise three.
 */
export const listViewRetry = (failureCount: number, error: Error): boolean => {
  if (error instanceof AppointmentListViewCursorError) return false;
  if (error instanceof AppointmentListViewRetryableError)
    return failureCount < 1;
  return !isAuthCredentialError(error) && failureCount < 3;
};

export const useAppointmentsList = (params: AppointmentListParams) => {
  const { userGroup } = useAuthSession();
  const { user, isDemo } = useCurrentUser();
  const isProd = useIsProdEnv();
  const queryClient = useQueryClient();
  const { readPath, loading: readPathLoading } = useAppointmentListReadPath(
    params,
    userGroup,
  );

  const doctorID = user?.doctor?.id;

  // Stable per query (the dash memoises `params`), so the key's identity is
  // the query's: the cursor restart below keys on it.
  const queryKey = useMemo(
    () => appointmentListQueryKey(params, userGroup, readPath),
    [params, userGroup, readPath],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }): Promise<AppointmentListPage> => {
      // If user group is not loaded yet, or if user is doctor
      // and doctor ID is not available, do not fetch data
      if (!userGroup || (userGroup === AuthUserGroup.DOCTOR && !doctorID)) {
        return { appointments: [], nextToken: null };
      }

      const operationName =
        readPath === "opensearch"
          ? "appointments-list-view"
          : userGroup === AuthUserGroup.DOCTOR
            ? "list-appointments-for-doctor"
            : "list-appointments";

      // Timed here rather than on the client: the sort field selects the GSI,
      // so the cost of a sort option is this fetch. A throw skips the emit —
      // a failed query is not a latency sample.
      const queryStart = performance.now();

      const result = await withErrorReporting(
        async (): Promise<AppointmentListPage> => {
          if (readPath === "opensearch" && params.listView) {
            return fetchAppointmentListView({
              ...params.listView,
              is_demo: isDemo,
              page_size: LIST_VIEW_PAGE_SIZE,
              cursor: pageParam,
            });
          }
          if (userGroup === AuthUserGroup.DOCTOR) {
            return appointmentListService.listAppointmentsForDoctor(
              isProd,
              doctorID!,
              params.sorting,
              params.startDate,
              params.endDate,
              false, // Doctor view will never show empty dates
              params.inquiryStartDate,
              params.inquiryEndDate,
              pageParam || null,
              isDemo,
            );
          } else {
            return appointmentListService.listAppointmentsOptimized(
              isProd,
              params.sorting,
              params.startDate,
              params.endDate,
              params.includeEmptyDates,
              params.inquiryStartDate,
              params.inquiryEndDate,
              pageParam || null,
              params.filters,
              undefined, // limit - use default
              undefined, // minResults - use default
              isDemo,
            );
          }
        },
        {
          componentName: "AppointmentsList",
          operationName,
          extra: {
            userGroup: userGroup,
            hasDoctorId: !!doctorID,
          },
        },
      );

      // Doctors' DynamoDB query takes no filters at all. The list view sends
      // every filter, and its page scope, whoever asks.
      const serverFilterCount =
        readPath === "opensearch"
          ? Object.values(params.listView?.filters ?? {}).filter((values) =>
              Array.isArray(values) ? values.length : values,
            ).length + Object.keys(params.listView?.checklist ?? {}).length
          : userGroup === AuthUserGroup.DOCTOR
            ? 0
            : Object.values(params.filters ?? {}).filter(
                (values) => values?.length,
              ).length;
      analytics.distribution(
        METRICS.DASHVIEW_QUERY_DURATION,
        performance.now() - queryStart,
        {
          sort_field: params.sorting.field,
          sort_direction: params.sorting.direction,
          is_first_page: !pageParam,
          server_filter_bucket: filterCountBucket(serverFilterCount),
          read_path: readPath,
        },
      );

      // Seed the detail cache so a drawer/detail page paints immediately, then
      // mark it stale for the fuller detail query.
      //
      // Seed ONLY when nothing is cached. This list's selection set is strictly
      // poorer (its `medicalAssistant` has no `preVisitCallScheduleLink`), so
      // overwriting DOWNGRADES a record the detail query already fetched and
      // blanks those fields until the refetch lands — which flashed the previsit
      // drawer on every org-wide event. See `patchAppointmentDetailCache`.
      //
      // Not from the list view: its rows carry only the page's columns, too
      // little for a drawer to paint from.
      if (readPath === "dynamo") {
        result.appointments.forEach((appointment) => {
          const detailKey = appointmentKeys.detail(appointment.id);
          if (queryClient.getQueryData(detailKey) === undefined) {
            queryClient.setQueryData(detailKey, appointment);
          }
          void queryClient.invalidateQueries({
            queryKey: detailKey,
            exact: true,
          });
        });
      }

      return result;
    },
    getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
    initialPageParam: null as string | null,
    // Wait for user data to load before fetching appointments.
    // This ensures isDemo is correctly determined from user.doctor/assistant.isDemo
    // before the query runs, preventing a race condition where appointments would be
    // fetched with isDemo=false before the user's demo status is known.
    // The read path must be known too, or the first page goes to DynamoDB and
    // the second to the list view under a different key.
    enabled:
      !!userGroup &&
      !!user &&
      (userGroup !== AuthUserGroup.DOCTOR || !!doctorID) &&
      !readPathLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...(readPath === "opensearch" && { retry: listViewRetry }),
  });

  // A rejected cursor (the query it was minted for no longer matches: the
  // server's "today" rolled over, or the scope changed) restarts from page 1.
  // Resetting the query drops the loaded pages and refetches without a cursor.
  // Once per query until a cursor is accepted again: if the restart's own
  // cursor is rejected too, the error stands rather than looping reset →
  // page 1 → scroll → reject; once page 2 loads, the next stale cursor (the
  // following midnight on an open tab) restarts again.
  const cursorRejected = query.error instanceof AppointmentListViewCursorError;
  const cursorAccepted = !query.isError && (query.data?.pages.length ?? 0) > 1;
  const restartedForRef = useRef<typeof queryKey | null>(null);
  useEffect(() => {
    if (cursorAccepted) restartedForRef.current = null;
    if (!cursorRejected || restartedForRef.current === queryKey) return;
    restartedForRef.current = queryKey;
    void queryClient.resetQueries({ queryKey, exact: true });
  }, [cursorRejected, cursorAccepted, queryClient, queryKey]);

  const total = query.data?.pages.at(-1)?.total;

  return { ...query, readPath, total };
};

const EMPTY_FILES_MAP = new Map<string, FileDisplayData[]>();

/**
 * Wrapper around useAppointmentsList that prefetches file data for all loaded
 * appointments. This ensures file data is in the React Query cache before
 * checklist filters run, preventing incorrect filter results from missing
 * file data (e.g., Panel or N110 status computed with empty files).
 *
 * Use this instead of useAppointmentsList in views with checklist filters
 * that depend on file data. Pass `fetchFiles: false` to skip file prefetching
 * (behaves identically to useAppointmentsList).
 */
export const useAppointmentsListWithFiles = (
  params: AppointmentListParams,
  options?: {
    /** A function sees the read path, which decides what the dash still
     *  computes in the browser and so whether files are needed. */
    fetchFiles?: boolean | ((readPath: AppointmentListReadPath) => boolean);
  },
) => {
  const result = useAppointmentsList(params);
  const fetchFiles =
    typeof options?.fetchFiles === "function"
      ? options.fetchFiles(result.readPath)
      : (options?.fetchFiles ?? true);
  // Wave 2: the page's rows go out as batch requests instead of one junction
  // read each.
  const { isEnabled: batchFileReads, loading: batchFlagLoading } =
    useBatchFileReads();
  const filesQueryConfig = batchFileReads
    ? appointmentFilesBatchQueryConfig
    : appointmentFilesQueryConfig;

  // The list view has no point-in-time snapshot, so a row whose sort fields
  // change between two page fetches can come back on the next page; keep the
  // first copy (spec D8).
  const appointments = useMemo(() => {
    const pages = result.data?.pages ?? [];
    if (result.readPath === "dynamo") {
      return pages.flatMap((page) => page.appointments);
    }
    const seen = new Set<string>();
    return pages.flatMap((page) =>
      page.appointments.filter((appointment) => {
        if (seen.has(appointment.id)) return false;
        seen.add(appointment.id);
        return true;
      }),
    );
  }, [result.data, result.readPath]);

  // The list view's checklist statuses for every loaded row: the cells read
  // them instead of the stored map or the ops-todos batch.
  const servedChecklist = useMemo<ServedChecklist>(() => {
    if (result.readPath === "dynamo") return null;
    const served = new Map<string, Readonly<Record<string, string>>>();
    for (const page of result.data?.pages ?? []) {
      for (const [id, statuses] of Object.entries(page.checklist ?? {})) {
        if (!served.has(id)) served.set(id, statuses);
      }
    }
    return served;
  }, [result.data, result.readPath]);

  // The list view's case readiness for every loaded row on the views that
  // read it: the row has no `caseInfo` or attorney data to compute it from.
  const servedCaseReady = useMemo<ServedCaseReady>(() => {
    if (result.readPath === "dynamo") return null;
    const served = new Map<string, boolean>();
    for (const page of result.data?.pages ?? []) {
      for (const [id, ready] of Object.entries(page.caseReady ?? {})) {
        if (!served.has(id)) served.set(id, ready);
      }
    }
    return served;
  }, [result.data, result.readPath]);

  // Prefetch files for all loaded appointments into the React Query cache.
  // When fetchFiles is false, or the read path isn't known yet, passes an
  // empty array so no queries run.
  const fileQueries = useQueries({
    queries:
      fetchFiles && !batchFlagLoading
        ? appointments.map((appointment) => filesQueryConfig(appointment.id))
        : [],
  });

  // Build a map of appointmentId → files. useQueries returns a new array
  // reference every render, so we use a ref to maintain a stable map reference
  // and only swap it when the underlying data actually changes. This prevents
  // downstream memos (filteredAppointments) from recomputing on every render.
  const appointmentFilesRef = useRef(EMPTY_FILES_MAP);

  const nextMap = new Map<string, FileDisplayData[]>();
  if (fetchFiles) {
    appointments.forEach((appointment, index) => {
      const data = fileQueries[index]?.data;
      if (data) {
        nextMap.set(appointment.id, data);
      }
    });
  }

  const prevMap = appointmentFilesRef.current;
  let mapChanged = nextMap.size !== prevMap.size;
  if (!mapChanged) {
    for (const [id, data] of nextMap) {
      if (prevMap.get(id) !== data) {
        mapChanged = true;
        break;
      }
    }
  }
  if (mapChanged) {
    appointmentFilesRef.current = nextMap;
  }

  const appointmentFiles = appointmentFilesRef.current;

  return {
    ...result,
    appointments,
    appointmentFiles,
    servedChecklist,
    servedCaseReady,
  };
};

// Hook for fetching appointment ranges (used for navigation, not caching)
export const useFetchAppointmentRange = () => {
  const isProd = useIsProdEnv();
  const { isDemo } = useCurrentUser();

  return useCallback(
    async (
      physicianId: string | null,
      start: Date | undefined,
      end: Date | undefined,
    ): Promise<AppointmentRecord[]> => {
      if (!physicianId) return [];

      // Direct fetch without caching - used only for navigation
      return fetchSchedulerAssistantAppointments(
        start,
        end,
        physicianId,
        isProd,
        { suppressErrors: false },
        isDemo,
      );
    },
    [isProd, isDemo],
  );
};

// Hook for fetching appointments for a single week
export const useWeekAppointments = (
  weekStart: Date | undefined,
  physicianId: string | null,
  enabled = true,
) => {
  const isProd = useIsProdEnv();
  const { isDemo } = useCurrentUser();

  const weekStartStr = weekStart?.toISOString().split("T")[0] || "";

  return useQuery({
    queryKey: appointmentKeys.schedulerWeek(physicianId, weekStartStr),
    queryFn: async (): Promise<AppointmentRecord[]> => {
      if (!physicianId || !weekStart) {
        return [];
      }

      // Calculate week end (6 days after start for Monday-Sunday week)
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      return withErrorReporting(
        () =>
          fetchSchedulerAssistantAppointments(
            weekStart,
            weekEnd,
            physicianId,
            isProd,
            { suppressErrors: false },
            isDemo,
          ),
        {
          componentName: "SchedulerAssistant",
          operationName: "fetch-week-appointments",
          extra: {
            physicianId,
            weekStart: weekStartStr,
          },
        },
      );
    },
    enabled: enabled && !!physicianId && !!weekStart,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Utility hook to prefetch a single week
export const usePrefetchSchedulerWeek = () => {
  const queryClient = useQueryClient();
  const isProd = useIsProdEnv();
  const { isDemo } = useCurrentUser();

  return useCallback(
    async (physicianId: string | null, weekStart: Date | undefined) => {
      if (!physicianId || !weekStart) return [];

      const weekStartStr = weekStart.toISOString().split("T")[0];
      const queryKey = appointmentKeys.schedulerWeek(physicianId, weekStartStr);

      // Check if we already have this data cached
      const cachedData =
        queryClient.getQueryData<AppointmentRecord[]>(queryKey);
      if (cachedData) {
        return cachedData;
      }

      // Calculate week end (6 days after start for Monday-Sunday week)
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      // Fetch and cache the data
      const data = await queryClient.fetchQuery({
        queryKey,
        queryFn: () =>
          fetchSchedulerAssistantAppointments(
            weekStart,
            weekEnd,
            physicianId,
            isProd,
            { suppressErrors: false },
            isDemo,
          ),
        staleTime: 2 * 60 * 1000,
      });

      return data;
    },
    [queryClient, isProd, isDemo],
  );
};
